import ArrowBackIcon from '@navi/web-ui/lib/icons/ArrowBackIcon/ArrowBackIcon';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import { Button, OTPInput, Typography } from '@navi/web-ui/lib/primitives';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import NaviNewLogoIcon from '@navi/web-ui/lib/icons/NaviLogoIcon/NaviNewLogoIcon';
import { Countdown } from '../../../../components/Countdown/Countdown';
import { RootState } from '../../../../store';
import { sendOTP, verifyOTP } from '../../AuthActions';
import { OTPInputScreenConstants } from '../../constants/AuthConstants';
import {
  resetGlobalError,
  setGlobalError,
  setShowOTPScreen,
  setVerifyOTPError
} from '../../reducers/authSlice';
import styles from '../Login.module.scss';
import { LoginErrorMessage } from './ErrorMessage';
import { getDeviceId } from 'src/utils/FingerPrintJS';
import Recaptcha from './Recaptcha';
import { setCaptchaVerified } from 'src/reducers/commonSlice';
import logger from '@cp/src/utils/logger';

interface OTPInputProps {
  navigateToCases: () => void;
}

const OTPInputScreen = ({ navigateToCases }: OTPInputProps) => {
  const phoneNumber = useSelector((state: RootState) => state?.auth?.mobileNumber);
  const otpToken = useSelector((state: RootState) => state?.auth?.otpToken);
  const captchaVerified = useSelector(
    (state: RootState) => state?.common?.userData?.captchaVerified
  );
  const showCaptcha =
    useSelector((state: RootState) => state?.common?.userData?.showCaptcha) || false;
  const verifyOTPError = useSelector((state: RootState) => state?.auth?.verifyOTPError);
  const isLoading = useSelector((state: RootState) => state?.auth?.isLoading);
  const fcmToken = useSelector((state: RootState) => state?.common?.fcmToken) || '';

  const dispatch = useDispatch();

  const [otpValue, setOTPValue] = React.useState<string>('');
  const [isPristine, setIsPristine] = React.useState<boolean>(true);
  const [countDownComplete, setCountDownComplete] = React.useState<boolean>(false);
  const [otpDescription, setOtpDescription] = React.useState<string>(
    OTPInputScreenConstants.OTPMessageBeforeTimeout
  );

  const handleBackButtonClick = () => {
    dispatch(setShowOTPScreen({ status: false }));
  };

  const handleOTPChange = (newOtpValue: string): void => {
    isPristine && setIsPristine(false);
    setOtpDescription(OTPInputScreenConstants.OTPMessageOnInput);
    setOTPValue(newOtpValue);
    dispatch(setVerifyOTPError(null));
    dispatch(resetGlobalError());
  };

  const getButtonDisabledState = (): boolean => {
    return !(otpValue?.length === 4) || isLoading || (showCaptcha && !captchaVerified);
  };

  const handleVerifyOTP = (event: React.SyntheticEvent): void => {
    logger('on the login page trying to login');
    event.preventDefault();
    dispatch(
      verifyOTP(
        {
          otp: otpValue,
          otpToken,
          source: 'WEB',
          deviceId: getDeviceId(),
          phoneNumber,
          fcmToken,
          captchaVerified
        },
        navigateToCases
      )
    );
  };

  const handleResendOTP = (): void => {
    setOTPValue('');
    dispatch(sendOTP({ phoneNumber: phoneNumber || '' }));
    setCountDownComplete(false);
    sendOTP({ phoneNumber: phoneNumber || '' });
  };

  const handleVerify = (success: boolean) => {
    setCaptchaVerified(success);
    dispatch(setCaptchaVerified(success));
  };

  React.useEffect(() => {
    if (verifyOTPError) {
      setOtpDescription(OTPInputScreenConstants.InvalidOTP);
      return;
    }
    if (otpValue?.length > 1 && otpDescription !== OTPInputScreenConstants.OTPMessageOnInput) {
      setOtpDescription(OTPInputScreenConstants.OTPMessageOnInput);
    }
  }, [otpValue, verifyOTPError]);

  useEffect(() => {
    dispatch(resetGlobalError());
  }, []);

  return (
    <>
      <GridRow className={styles.icon}>
        <GridColumn>
          <ArrowBackIcon size="md" color="#606367" onClick={handleBackButtonClick} />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn md>
          <NaviNewLogoIcon size="md" />
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn xs>
          <Typography variant="h1">{OTPInputScreenConstants.title}</Typography>
        </GridColumn>
      </GridRow>
      <GridRow>
        <GridColumn xs>
          <Typography className={styles.subTitle} variant="p3" as="span">
            {OTPInputScreenConstants.subTitle} <b>{phoneNumber}</b>{' '}
            <Button
              variant="text"
              size="small"
              onClick={handleBackButtonClick}
              className={styles['auth-wrapper__login-wrapper__otp-subtitle']}
            >
              {OTPInputScreenConstants.changeButtonText}
            </Button>
          </Typography>
        </GridColumn>
      </GridRow>
      <form onSubmit={handleVerifyOTP}>
        <GridRow>
          <GridColumn xs className={styles['auth-wrapper__login-wrapper__otp-container']}>
            <OTPInput
              onChange={handleOTPChange}
              error={!isPristine && (verifyOTPError?.length > 0 || otpValue.length < 4)}
              isInputSecure
              value={otpValue}
              autoFocus
            />
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn xs>
            {countDownComplete ? (
              <Typography className={styles.hintMessage} variant="p3" as="span">
                {OTPInputScreenConstants.OTPMessageOnInput}{' '}
                <Button
                  variant="text"
                  size="small"
                  onClick={handleResendOTP}
                  disabled={!countDownComplete}
                  type="button"
                >
                  {OTPInputScreenConstants.ResendOTP}
                </Button>
              </Typography>
            ) : (
              <Typography className={styles.hintMessage} variant="p3" as="span">
                {OTPInputScreenConstants.OTPMessageBeforeTimeout}{' '}
                <Countdown
                  onComplete={() => {
                    setCountDownComplete(true);
                  }}
                  startFrom={30}
                />{' '}
                second
              </Typography>
            )}
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn md>{showCaptcha && <Recaptcha onVerify={handleVerify} />}</GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn md>
            <div className={styles['auth-wrapper__login-wrapper__bottom-cta']}>
              <Button size="large" fullWidth disabled={getButtonDisabledState()} type="submit">
                {isLoading
                  ? OTPInputScreenConstants.VerifyingOTP
                  : OTPInputScreenConstants.VerifyOTP}
              </Button>
            </div>
          </GridColumn>
        </GridRow>
        <LoginErrorMessage />
      </form>
    </>
  );
};

export default OTPInputScreen;
